import { ESourceRefreshFrequency } from '../../ducks/api-ducks/data-refresh-details-duck/types-data-refresh-details';

type TTranslate = (key: string) => string;

export type TSourceNameMappingEntry = {
  refreshExplanationKey?: string;
  refreshExplanationFEDKey?: string;
  displayNameKey?: string;
  nextRefreshTextKey?: string;
};

export const sourceNameMapping: Record<string, TSourceNameMappingEntry> = {
  'Adjustment Personnel_IM': {
    refreshExplanationKey: 'dataRefreshPanelAdjustmentDataSourceSourceFrequencyDesc',
  },
  'Azure Health Report': {
    refreshExplanationKey: 'dataRefreshPanelAzureHealthReportSourceFrequencyDesc',
  },
  'CCCV Insights Dashboard': {
    refreshExplanationKey: 'dataRefreshPanelCCCVInsightsDashboardSourceFrequencyDesc',
    refreshExplanationFEDKey: 'dataRefreshPanelCCCVInsightsDashboardSourceFrequencyDesc_Fed',
  },
  GPSMart: {
    refreshExplanationKey: 'dataRefreshPanelGPSMartSourceFrequencyDesc',
  },
  'IDEAs and FDandE': {
    refreshExplanationKey: 'dataRefreshPanelIDEAsAndFDandESourceFrequencyDesc',
  },
  'ISD Deal Three Sixty Insights Dashboard': {
    refreshExplanationKey: 'dataRefreshPanelISDDealThreeSixtyInsightsDashboardSourceFrequencyDesc',
  },
  MHR: {
    refreshExplanationKey: 'dataRefreshPanelMHRSourceFrequencyDesc',
  },
  'MS Sales': {
    refreshExplanationKey: 'dataRefreshPanelMSSalesSourceFrequencyDesc',
  },
  MSSales: {
    refreshExplanationKey: 'dataRefreshPanelMSSalesSourceFrequencyDesc',
  },
  OneChannel: {
    refreshExplanationKey: 'dataRefreshPanelOneChannelSourceFrequencyDesc',
  },
  'PS DataLake': {
    refreshExplanationKey: 'dataRefreshPanelPSDataLakeSourceFrequencyDesc',
  },
  'Product Finder': {
    refreshExplanationKey: 'dataRefreshPanelProductFinderSourceFrequencyDesc',
    nextRefreshTextKey: 'dataRefreshPanelProductFinderNextRefreshDate',
  },
  'Seller Revenue Credit Cube': {
    refreshExplanationKey: 'dataRefreshPanelSellerRevenueCreditCubeSourceFrequencyDesc',
  },
  'UCM MSA & TrueCRM': {
    refreshExplanationKey: 'dataRefreshPanelUCMMSAAndTrueCRMSourceFrequencyDesc',
  },
  'UCM MSA': {
    refreshExplanationKey: 'dataRefreshPanelUCMMSAAndTrueCRMSourceFrequencyDesc',
  },
  'Unified Consumed Revenue Dashboard': {
    refreshExplanationKey: 'dataRefreshPanelUnifiedConsumedRevenueDashboardSourceFrequencyDesc',
  },
  'Marketplace Billed Sales Field Tracking Report': {
    refreshExplanationKey: 'dataRefreshPanelMarketplaceBilledSalesFieldTrackingReportSourceFrequencyDesc',
  },
  'Starlight AHR': {
    refreshExplanationFEDKey: 'dataRefreshPanelStarlightAHRSourceFrequencyDesc_Fed',
  },
  'Corp ContractValue': {
    refreshExplanationFEDKey: 'dataRefreshPanelCorpContractValueSourceFrequencyDesc_Fed',
  },
  'Fed Seller Revenue Credit Cube': {
    refreshExplanationFEDKey: 'dataRefreshPanelFedSellerRevenueCreditCubeSourceFrequencyDesc_Fed',
  },
  MFRR: {
    refreshExplanationFEDKey: 'dataRefreshPanelMFRRSourceFrequencyDesc_Fed',
  },
  'Sales Partner Insights & FD+E': {
    refreshExplanationFEDKey: 'dataRefreshPanelSalesPartnerInsightsSourceFrequencyDesc_Fed',
  },
  'Corp Unified Consumed Revenue Dashboard': {
    refreshExplanationFEDKey: 'dataRefreshPanelCorpUnifiedConsumedRevenueDashboardSourceFrequencyDesc_Fed',
  },
  'Corp PSDL': {
    refreshExplanationFEDKey: 'dataRefreshPanelCorpPSDLSourceFrequencyDesc_Fed',
  },
  'Federal SAP': {
    refreshExplanationFEDKey: 'dataRefreshPanelFederalSAPSourceFrequencyDesc_Fed',
  },
  'Federal SAP & Compass2': {
    refreshExplanationFEDKey: 'dataRefreshPanelFederalSAPCompassSourceFrequencyDesc_Fed',
  },
  mintx: {
    displayNameKey: 'dataRefreshPanelTerritoryDataSourceName',
    refreshExplanationKey: 'dataRefreshPanelTerritoryDataSourceFrequencyDesc',
    refreshExplanationFEDKey: 'dataRefreshPanelTerritoryDataSourceFrequencyDesc_Fed',
  },
  MSXi: {
    refreshExplanationKey: 'dataRefreshPanelMSXiSourceFrequencyDesc',
  },
};

/**
 * Resolves the refresh explanation for a source.
 */
export const getSourceRefreshExplanation = (sourceName: string, t: TTranslate, isFed: boolean = false): string | undefined => {
  const sourceConfig = sourceNameMapping[sourceName];
  const translationKey =
    isFed && sourceConfig?.refreshExplanationFEDKey ? sourceConfig.refreshExplanationFEDKey : sourceConfig?.refreshExplanationKey;
  return translationKey ? t(translationKey) : undefined;
};

export const getSourceDisplayName = (sourceName: string, t: TTranslate): string => {
  const sourceConfig = sourceNameMapping[sourceName];
  return sourceConfig?.displayNameKey ? t(sourceConfig.displayNameKey) : sourceName;
};

export const getSourceNextRefreshText = (sourceName: string, t: TTranslate): string | undefined => {
  const translationKey = sourceNameMapping[sourceName]?.nextRefreshTextKey;
  return translationKey ? t(translationKey) : undefined;
};

/**
 * Maps an API frequency value to the i18n key used for its short display label
 * (e.g. "Weekly", "Multiple/week", "Monthly").
 */
export const frequencyLabelKeyMap: Record<string, string> = {
  [ESourceRefreshFrequency.Weekly]: 'dataRefreshFrequencyWeekly',
  [ESourceRefreshFrequency.Monthly]: 'dataRefreshFrequencyMonthly',
  [ESourceRefreshFrequency.MultipleTimesPerWeek]: 'dataRefreshFrequencyMultipleTimesPerWeek',
  [ESourceRefreshFrequency.NotScheduled]: 'dataRefreshFrequencyNotScheduled',
  [ESourceRefreshFrequency.OnDemand]: 'dataRefreshFrequencyOnDemand',
};

export const getFrequencyLabelKey = (frequency?: string): string | undefined => (frequency ? frequencyLabelKeyMap[frequency] : undefined);
